import { z } from 'zod/v4';

import { $ISODate, $PermissionLevel, $TabularColumnSummary } from './columns';
import { $DatasetLicenses } from './licenses';

const $DatasetViewPagination = z.object({
  currentPage: z.int().gte(1),
  itemsPerPage: z.int().gte(1)
});
type $DatasetViewPagination = z.infer<typeof $DatasetViewPagination>;

const $DatasetType = z.enum(['BASE', 'BINARY', 'TABULAR']);
type $DatasetType = z.infer<typeof $DatasetType>;

const $DatasetStatus = z.enum(['Fail', 'Processing', 'Success']);
type $DatasetStatus = z.infer<typeof $DatasetStatus>;

const $CreateDataset = z.object({
  datasetType: $DatasetType,
  description: z.string().optional(),
  isJSON: z.enum(['true', 'false']),
  isReadyToShare: z.enum(['true', 'false']),
  license: $DatasetLicenses,
  name: z.string(),
  permission: $PermissionLevel,
  // a multipart field that occurs exactly once is received as a string, rather than an array of length one
  primaryKeys: z
    .union([z.string(), z.string().array()])
    .optional()
    .default([])
    .transform((primaryKeys) => (typeof primaryKeys === 'string' ? [primaryKeys] : primaryKeys))
});
type $CreateDataset = z.infer<typeof $CreateDataset>;

const $EditDatasetInfo = z.object({
  description: z.string().optional(),
  license: $DatasetLicenses.optional(),
  name: z.string().optional(),
  permission: $PermissionLevel.optional()
});
type $EditDatasetInfo = z.infer<typeof $EditDatasetInfo>;

const $DatasetInfo = z.object({
  createdAt: $ISODate,
  datasetType: $DatasetType,
  description: z.string().nullable(),
  id: z.string(),
  isReadyToShare: z.boolean(),
  license: $DatasetLicenses,
  managerIds: z.string().array(),
  name: z.string(),
  permission: $PermissionLevel,
  status: $DatasetStatus,
  updatedAt: $ISODate
});
type $DatasetInfo = z.infer<typeof $DatasetInfo>;

const $DatasetCardProps = $DatasetInfo.and(z.object({ isManager: z.boolean(), isPublic: z.boolean() }));
type $DatasetCardProps = z.infer<typeof $DatasetCardProps>;

const $TabularDataRow = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
type $TabularDataRow = z.infer<typeof $TabularDataRow>;

const $TabularDatasetView = z.object({
  columnIds: z.record(z.string(), z.string()),
  columns: z.string().array(),
  metadata: z.record(z.string(), $TabularColumnSummary),
  primaryKeys: z.string().array(),
  rows: $TabularDataRow.array(),
  totalNumberOfColumns: z.number().min(0),
  totalNumberOfRows: z.number().min(0)
});
type $TabularDatasetView = z.infer<typeof $TabularDatasetView>;

const $ProjectTabularDatasetView = $TabularDatasetView.omit({
  primaryKeys: true
});
type $ProjectTabularDatasetView = z.infer<typeof $ProjectTabularDatasetView>;

const $TabularDataset = $DatasetInfo.and($TabularDatasetView);
type $TabularDataset = z.infer<typeof $TabularDataset>;

export {
  $CreateDataset,
  $DatasetCardProps,
  $DatasetInfo,
  $DatasetLicenses,
  $DatasetStatus,
  $DatasetViewPagination,
  $EditDatasetInfo,
  $PermissionLevel,
  $ProjectTabularDatasetView,
  $TabularDataset,
  $TabularDatasetView
};
